import React from "react";
import classNames from "classnames";
import Icon from "assets/icons";
import { TopNoteComponentType, TopNoteConfig } from "./topNoteTypes";
import LocalStorage from "shared/utils/storage/LocalStorage";
import { AppType, LocalStorageKey } from "shared/utils/enums";
import { getEnvironmentInfo } from "shared/utils/config";
import { getChatBotElement, modifyTopNoteStyleForChatBot } from "../chatBot/util";
import {
  AnalyticsContextEntityTypeEnum,
  AnalyticsEventNameEnum,
  AnalyticsScreenNameEnum
} from "shared/enums/AnalyticsEnums";
import AnalyticsDatastoreType from "shared/enums/AnalyticsDatastoreType";

/**
 * @description Component to display the display-notes at various reg/my-exp screens
 * @param onClickHandler
 * @param props mainly a storage-key which is a unique identifier or the screen/reg-form, to mark that the display note is read by the user or not.
 * @todo Add snippet to handle display note API - to be done after discussion over how we will handle display notes in myExp.
 */

function TopNote({ onClickHandlerHook, ...props }: TopNoteConfig) {
  const [expanded, setExpanded] = React.useState(false);

  React.useEffect(() => {
    if (expanded) {
      props.logEvent?.(
        AnalyticsEventNameEnum.SCREEN_VIEW,
        {
          screenType: AnalyticsScreenNameEnum.DISPLAY_NOTE,
          contextEntityType: AnalyticsContextEntityTypeEnum.DISPLAY_NOTE,
          contextEntityId: props.module
        },
        AnalyticsDatastoreType.MIXPANEL_BUSINESS
      );
    }
    // eslint-disable-next-line
  }, [expanded]);

  const handleDisplayNoteClick = React.useCallback(() => {
    setExpanded(expandedState => {
      const newValue = !expandedState;
      onClickHandlerHook?.(newValue);
      return newValue;
    });
  }, [onClickHandlerHook]);

  const getDisplayNotesFromStorage = React.useCallback(() => {
    const keyStringFromStorage = LocalStorage.getItem(LocalStorageKey.DISPLAY_NOTE);
    return keyStringFromStorage && JSON.parse(keyStringFromStorage);
  }, []);

  const isDisplayNoteAlreadyViewed = React.useMemo(() => {
    const displayNoteKeys = getDisplayNotesFromStorage();
    return displayNoteKeys && displayNoteKeys?.includes(props.storageKey);
  }, [getDisplayNotesFromStorage, props.storageKey]);

  React.useEffect(() => {
    // if we are previewing/filling up a reg form then we don't want to store the key in localStorage and always show the top note
    if (getEnvironmentInfo().appType === AppType.REGISTRATION) {
      setExpanded(true);
      return;
    }
    if (!isDisplayNoteAlreadyViewed) {
      setExpanded(true);
      const displayNoteKeys = getDisplayNotesFromStorage();
      displayNoteKeys?.push(props.storageKey);
      const keysForStorage = JSON.stringify(displayNoteKeys ? displayNoteKeys : [props.storageKey]);
      LocalStorage.setItem(LocalStorageKey.DISPLAY_NOTE, keysForStorage);
    }
  }, [isDisplayNoteAlreadyViewed, getDisplayNotesFromStorage, props.storageKey]);

  return (
    <TopNoteComponent
      body={props.body}
      expanded={expanded}
      handleClick={handleDisplayNoteClick}
      className={props.className}
      noteList={props.noteList}
    />
  );
}

function TopNoteComponent(props: TopNoteComponentType) {
  const computedClassNames = React.useMemo(() => {
    return classNames(props.className, {
      topNote: true,
      "topNote--expanded": props.expanded
    });
  }, [props.expanded, props.className]);

  React.useEffect(() => {
    if (!props.body) {
      return;
    }
    const chatBotElem = getChatBotElement();
    chatBotElem && modifyTopNoteStyleForChatBot();
  }, [props.body]);

  return (
    <div className="topNote_root">
      {props.body ? (
        <React.Fragment>
          <div className={computedClassNames}>
            <div
              key={String(props.expanded)}
              className="topNote_content"
              dangerouslySetInnerHTML={{
                __html: props.body
              }}></div>
            <button className="topNote_toggle" onClick={props.handleClick}>
              {props.expanded ? <Icon.Close /> : <Icon.Bulb />}
            </button>
          </div>
          <div className="topNote_overlay"></div>
        </React.Fragment>
      ) : null}

      {Boolean(props.noteList?.length) ? (
        <React.Fragment>
          <div className={computedClassNames}>
            <div className="topNote_content">
              {props.noteList?.map((note, index) => {
                return (
                  <div className="topNote_contentItem" key={index}>
                    <h3 className="topNote_content--heading">{note.entityName}</h3>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: note.body
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <button className="topNote_toggle" onClick={props.handleClick}>
              {props.expanded ? <Icon.Close /> : <Icon.Bulb />}
            </button>
          </div>

          <div className="topNote_overlay"></div>
        </React.Fragment>
      ) : null}
    </div>
  );
}

export default TopNote;
